import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FiX } from 'react-icons/fi';
import { API_BASE, authHeaders, formatSgDate, formatAssetStatus } from '../../utils/assetHelpers';

function InfoCard({ title, children, className = 'bg-slate-50' }) {
    return (
        <section className={`rounded-lg border border-gray-200 p-4 ${className}`}>
            <h3 className="text-sm font-semibold text-gray-800 mb-3">{title}</h3>
            <div className="space-y-2">{children}</div>
        </section>
    );
}

function InfoRow({ label, value }) {
    if (value == null || value === '') return null;
    return (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-1">
            <span className="text-sm font-medium text-gray-500">{label}</span>
            <span className="sm:col-span-2 text-sm text-gray-900 break-words whitespace-pre-wrap">{value}</span>
        </div>
    );
}

const ViewServerAssetModal = ({ asset, onClose, onEdit }) => {
    const [detail, setDetail] = useState(asset || null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!asset?._id) return undefined;
        let cancelled = false;
        setLoading(true);
        axios.get(`${API_BASE}/api/assets/servers/${asset._id}`, { headers: authHeaders() })
            .then((res) => { if (!cancelled && res.data.success) setDetail(res.data.data); })
            .catch((err) => { if (!cancelled) setError(err.response?.data?.error || err.message); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [asset?._id]);

    const display = detail || asset;
    const subServers = display?.subServerObject || [];

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto p-4">
            <div className="bg-white rounded-lg p-6 w-full max-w-4xl my-8 min-w-0">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-bold">View Server Asset</h2>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700"><FiX className="w-6 h-6" /></button>
                </div>
                {error && <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-md">{error}</div>}
                {loading ? <p className="text-gray-600">Loading...</p> : (
                    <div className="space-y-4">
                        <InfoCard title="Main Server" className="bg-blue-50">
                            <InfoRow label="Server Name" value={display?.mainServerName} />
                            <InfoRow label="Type" value={display?.serverType} />
                            <InfoRow label="Status" value={formatAssetStatus(display?.status)} />
                            <InfoRow label="Login ID" value={display?.loginID} />
                            <InfoRow label="Login Password" value={display?.loginPass} />
                            <InfoRow label="Remarks" value={display?.remarks} />
                        </InfoCard>

                        {subServers.length === 0 ? (
                            <InfoCard title="Sub Servers" className="bg-violet-50">
                                <p className="text-sm text-gray-500">No sub servers.</p>
                            </InfoCard>
                        ) : (
                            subServers.map((sub, i) => {
                                const domainLines = (sub.domains || [])
                                    .map((d) => (typeof d === 'object' ? d.domainURL : d))
                                    .filter(Boolean)
                                    .join('\n');
                                const estAmount = [sub.estMonthlyPaymentCurrency, sub.estMonthlyPaymentAmount]
                                    .filter((v) => v !== '' && v != null)
                                    .join(' ');
                                return (
                                    <InfoCard key={sub._id || i} title={`Sub Server ${i + 1}`} className="bg-violet-50">
                                        <InfoRow label="Name" value={sub.name} />
                                        <InfoRow label="Type" value={sub.type} />
                                        <InfoRow label="Status" value={formatAssetStatus(sub.status)} />
                                        <InfoRow label="Est. Monthly" value={estAmount} />
                                        <InfoRow label="Login ID" value={sub.loginID} />
                                        <InfoRow label="Login Password" value={sub.loginPass} />
                                        <InfoRow label="Linked Domains" value={domainLines} />
                                    </InfoCard>
                                );
                            })
                        )}

                        <InfoCard title="Payment History" className="bg-emerald-50">
                            {(display?.paymentHistory || []).length === 0 ? (
                                <p className="text-sm text-gray-500">No payment records.</p>
                            ) : (
                                (display.paymentHistory || []).map((row, i) => (
                                    <InfoRow
                                        key={i}
                                        label={`Payment ${i + 1}`}
                                        value={`${row.currency || '-'} ${Number(row.amount || 0).toFixed(2)} on ${formatSgDate(row.paymentDate)}`}
                                    />
                                ))
                            )}
                        </InfoCard>
                    </div>
                )}
                <div className="flex justify-end gap-3 mt-6">
                    {onEdit && display && (
                        <button type="button" onClick={() => onEdit(display)} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">Edit</button>
                    )}
                    <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50">Close</button>
                </div>
            </div>
        </div>
    );
};

export default ViewServerAssetModal;
